import React, { useState, useEffect } from 'react';
import {
  Box, Button, Grid, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Fab, Alert, Pagination,
  FormControl, InputLabel, Select, MenuItem, Tooltip, useTheme, useMediaQuery, Avatar,
} from '@mui/material';
import {
  Add, Edit, Delete, Search, Gavel, History, DirectionsCar,
  CheckCircle, PauseCircle, Build, Refresh, Warning,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import frotaService from '../../services/frotaService';
import { Veiculo, VeiculoMulta, AlertaTrocaOleo } from '../../types/frota';
import VeiculoForm from '../../components/forms/VeiculoForm';
import VeiculoHistoricoDialog from '../../components/frota/VeiculoHistoricoDialog';
import PlacaBrasil from '../../components/frota/PlacaBrasil';
import FrotaPageLayout, { FrotaStatItem } from '../../components/frota/FrotaPageLayout';
import FuncionariosSelect from '../../components/selects/FuncionariosSelect';
import {
  formatarValorOnInput, parsearValorFormatado,
} from '../../utils/formatToView';

const STATUS_COLORS = {
  ativo: '#4CAF50',
  inativo: '#9E9E9E',
  manutencao: '#FF9800',
} as const;

const VeiculosPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, ativo: 0, inativo: 0, manutencao: 0 });
  const [alertasTrocaOleo, setAlertasTrocaOleo] = useState<Record<number, AlertaTrocaOleo>>({});
  const [openForm, setOpenForm] = useState(false);
  const [selected, setSelected] = useState<Veiculo | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [openMultas, setOpenMultas] = useState(false);
  const [openHistorico, setOpenHistorico] = useState(false);
  const [openFoto, setOpenFoto] = useState(false);
  const [fotoAmpliada, setFotoAmpliada] = useState<{ url: string; placa: string } | null>(null);
  const [multas, setMultas] = useState<VeiculoMulta[]>([]);
  const [openMultaForm, setOpenMultaForm] = useState(false);
  const [multaForm, setMultaForm] = useState({
    data_multa: '', infracao: '', valor: 0, valorFormatado: '0,00',
    funcionario_id: null as number | null,
    pontos: '', data_vencimento: '', status: 'pendente', observacoes: '',
  });

  const loadStats = async () => {
    try {
      const [totalRes, ativoRes, inativoRes, manutencaoRes] = await Promise.all([
        frotaService.listarVeiculos({ limit: 1 }),
        frotaService.listarVeiculos({ status: 'ativo', limit: 1 }),
        frotaService.listarVeiculos({ status: 'inativo', limit: 1 }),
        frotaService.listarVeiculos({ status: 'manutencao', limit: 1 }),
      ]);
      setStats({
        total: totalRes.pagination?.total || 0,
        ativo: ativoRes.pagination?.total || 0,
        inativo: inativoRes.pagination?.total || 0,
        manutencao: manutencaoRes.pagination?.total || 0,
      });
    } catch {
      /* mantém stats anteriores */
    }
  };

  const loadAlertasTrocaOleo = async () => {
    try {
      const alertas = await frotaService.getAlertasTrocaOleo(100);
      const map: Record<number, AlertaTrocaOleo> = {};
      alertas.forEach((a) => { map[a.veiculo_id] = a; });
      setAlertasTrocaOleo(map);
    } catch {
      /* mantém alertas anteriores */
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await frotaService.listarVeiculos({ search, status, page, limit: 15 });
      setVeiculos(res.data || []);
      setTotalPages(res.pagination?.pages || 1);
    } catch {
      toast.error('Erro ao carregar veículos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); loadAlertasTrocaOleo(); }, []);
  useEffect(() => { load(); }, [search, status, page]);

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await frotaService.excluirVeiculo(selected.id);
      toast.success('Veículo excluído');
      setOpenDelete(false);
      load();
      loadStats();
    } catch {
      toast.error('Erro ao excluir');
    }
  };

  const openFotoModal = (v: Veiculo) => {
    if (!v.foto_url) return;
    setFotoAmpliada({ url: frotaService.getUploadUrl(v.foto_url), placa: v.placa });
    setOpenFoto(true);
  };

  const openHistoricoDialog = (v: Veiculo) => {
    setSelected(v);
    setOpenHistorico(true);
  };

  const openMultasDialog = async (v: Veiculo) => {
    setSelected(v);
    const res = await frotaService.listarMultas(v.id);
    setMultas(res.data || []);
    setOpenMultas(true);
  };

  const salvarMulta = async () => {
    if (!selected || !multaForm.data_multa || !multaForm.infracao) {
      toast.error('Preencha data e infração');
      return;
    }
    try {
      await frotaService.criarMulta({
        veiculo_id: selected.id,
        funcionario_id: multaForm.funcionario_id,
        data_multa: multaForm.data_multa,
        infracao: multaForm.infracao,
        valor: multaForm.valor,
        pontos: multaForm.pontos ? Number(multaForm.pontos) : null,
        data_vencimento: multaForm.data_vencimento || null,
        status: multaForm.status as VeiculoMulta['status'],
        observacoes: multaForm.observacoes,
      });
      toast.success('Multa registrada');
      setOpenMultaForm(false);
      const res = await frotaService.listarMultas(selected.id);
      setMultas(res.data || []);
    } catch {
      toast.error('Erro ao salvar multa');
    }
  };

  const handleFilterChange = (filterValue: string) => {
    setStatus(filterValue);
    setPage(1);
  };

  const statItems: FrotaStatItem[] = [
    { key: 'todos', label: 'Total', value: stats.total, icon: <DirectionsCar />, filterValue: '' },
    { key: 'ativo', label: 'Ativos', value: stats.ativo, color: STATUS_COLORS.ativo, icon: <CheckCircle />, filterValue: 'ativo' },
    { key: 'inativo', label: 'Inativos', value: stats.inativo, color: STATUS_COLORS.inativo, icon: <PauseCircle />, filterValue: 'inativo' },
    { key: 'manutencao', label: 'Manutenção', value: stats.manutencao, color: STATUS_COLORS.manutencao, icon: <Build />, filterValue: 'manutencao' },
  ];

  const statusColor = (s: string) => s === 'ativo' ? 'success' : s === 'manutencao' ? 'warning' : 'default';

  const tooltipAlertaTrocaOleo = (alerta: AlertaTrocaOleo) => {
    if (alerta.urgencia === 'vencido') {
      return `Troca de óleo vencida há ${Math.abs(alerta.km_restantes).toLocaleString('pt-BR')} km (KM atual: ${alerta.km_atual.toLocaleString('pt-BR')} · próxima: ${alerta.proxima_troca_km.toLocaleString('pt-BR')} km)`;
    }
    return `Faltam ${alerta.km_restantes.toLocaleString('pt-BR')} km para a troca de óleo (KM atual: ${alerta.km_atual.toLocaleString('pt-BR')} · próxima: ${alerta.proxima_troca_km.toLocaleString('pt-BR')} km)`;
  };

  return (
    <>
      <FrotaPageLayout
        title="Gestão de Veículos"
        subtitle="Cadastro, histórico, multas e acompanhamento da frota"
        icon={<DirectionsCar fontSize="large" />}
        iconColor="#2196F3"
        stats={statItems}
        activeFilter={status}
        onFilterChange={handleFilterChange}
        loading={loading}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => { load(); loadStats(); loadAlertasTrocaOleo(); }}
              sx={{ borderRadius: 2 }}
            >
              Atualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => { setSelected(null); setOpenForm(true); }}
              sx={{ borderRadius: 2, boxShadow: 2 }}
            >
              Novo Veículo
            </Button>
          </>
        }
        filters={
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth size="small" placeholder="Buscar placa, marca, modelo..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="ativo">Ativo</MenuItem>
                  <MenuItem value="inativo">Inativo</MenuItem>
                  <MenuItem value="manutencao">Manutenção</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        }
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700 }}>Foto</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Placa</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Veículo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Motorista</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>KM</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {veiculos.map((v) => (
                <TableRow key={v.id} hover>
                  <TableCell>
                    {v.foto_url ? (
                      <Tooltip title="Ampliar foto">
                        <Avatar
                          src={frotaService.getUploadUrl(v.foto_url)}
                          variant="rounded"
                          sx={{ width: 48, height: 36, cursor: 'pointer' }}
                          onClick={() => openFotoModal(v)}
                        />
                      </Tooltip>
                    ) : (
                      <Avatar variant="rounded" sx={{ width: 48, height: 36, bgcolor: 'grey.200' }}>
                        <DirectionsCar fontSize="small" color="disabled" />
                      </Avatar>
                    )}
                  </TableCell>
                  <TableCell><PlacaBrasil placa={v.placa} size="small" /></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {alertasTrocaOleo[v.id] && (
                        <Tooltip title={tooltipAlertaTrocaOleo(alertasTrocaOleo[v.id])}>
                          <Warning
                            fontSize="small"
                            color={alertasTrocaOleo[v.id].urgencia === 'vencido' ? 'error' : 'warning'}
                          />
                        </Tooltip>
                      )}
                      <span>{v.marca} {v.modelo} {v.ano ? `(${v.ano})` : ''}</span>
                    </Box>
                  </TableCell>
                  <TableCell>{v.motorista_atual?.nome || '-'}</TableCell>
                  <TableCell>{v.kilometragem_atual?.toLocaleString('pt-BR')} km</TableCell>
                  <TableCell><Chip size="small" label={v.status} color={statusColor(v.status) as any} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Histórico"><IconButton size="small" onClick={() => openHistoricoDialog(v)}><History /></IconButton></Tooltip>
                    <Tooltip title="Multas"><IconButton size="small" onClick={() => openMultasDialog(v)}><Gavel /></IconButton></Tooltip>
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => { setSelected(v); setOpenForm(true); }}><Edit /></IconButton></Tooltip>
                    <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => { setSelected(v); setOpenDelete(true); }}><Delete /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {veiculos.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center"><Alert severity="info">Nenhum veículo encontrado</Alert></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
          </Box>
        )}
      </FrotaPageLayout>

      {isMobile && (
        <Fab color="primary" sx={{ position: 'fixed', bottom: 80, right: 16 }} onClick={() => { setSelected(null); setOpenForm(true); }}>
          <Add />
        </Fab>
      )}

      <VeiculoForm open={openForm} onClose={() => setOpenForm(false)} onSuccess={() => { load(); loadStats(); loadAlertasTrocaOleo(); }} veiculo={selected} />

      <VeiculoHistoricoDialog
        open={openHistorico}
        onClose={() => setOpenHistorico(false)}
        veiculo={selected}
        onUpdated={load}
      />

      <Dialog open={openFoto} onClose={() => setOpenFoto(false)} maxWidth="md" fullWidth>
        <DialogTitle>Foto — {fotoAmpliada?.placa}</DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          {fotoAmpliada && (
            <Box
              component="img"
              src={fotoAmpliada.url}
              alt={`Foto do veículo ${fotoAmpliada.placa}`}
              sx={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 1, objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFoto(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>Excluir veículo?</DialogTitle>
        <DialogContent>Confirma exclusão de <strong>{selected?.placa}</strong>?</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Excluir</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openMultas} onClose={() => setOpenMultas(false)} maxWidth="md" fullWidth>
        <DialogTitle>Multas - {selected?.placa}</DialogTitle>
        <DialogContent>
          <Button size="small" startIcon={<Add />} sx={{ mb: 2 }} onClick={() => {
            setMultaForm({
              data_multa: new Date().toISOString().split('T')[0], infracao: '', valor: 0, valorFormatado: '0,00',
              funcionario_id: null,
              pontos: '', data_vencimento: '', status: 'pendente', observacoes: '',
            });
            setOpenMultaForm(true);
          }}>Nova multa</Button>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Motorista</TableCell>
                <TableCell>Infração</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {multas.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{m.data_multa}</TableCell>
                  <TableCell>{m.motorista?.nome || '-'}</TableCell>
                  <TableCell>{m.infracao}</TableCell>
                  <TableCell>{frotaService.formatCurrency(Number(m.valor))}</TableCell>
                  <TableCell><Chip size="small" label={m.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpenMultas(false)}>Fechar</Button></DialogActions>
      </Dialog>

      <Dialog open={openMultaForm} onClose={() => setOpenMultaForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Registrar multa</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FuncionariosSelect
                label="Motorista"
                value={multaForm.funcionario_id}
                onChange={(v) => setMultaForm({ ...multaForm, funcionario_id: v })}
              />
            </Grid>
            <Grid item xs={6}><TextField fullWidth type="date" label="Data" InputLabelProps={{ shrink: true }} value={multaForm.data_multa} onChange={(e) => setMultaForm({ ...multaForm, data_multa: e.target.value })} /></Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth type="text" label="Valor"
                value={multaForm.valorFormatado}
                onChange={(e) => {
                  const fmt = formatarValorOnInput(e.target.value);
                  setMultaForm({ ...multaForm, valorFormatado: fmt, valor: parsearValorFormatado(fmt) });
                }}
              />
            </Grid>
            <Grid item xs={12}><TextField fullWidth label="Infração" value={multaForm.infracao} onChange={(e) => setMultaForm({ ...multaForm, infracao: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth type="number" label="Pontos" value={multaForm.pontos} onChange={(e) => setMultaForm({ ...multaForm, pontos: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth type="date" label="Vencimento" InputLabelProps={{ shrink: true }} value={multaForm.data_vencimento} onChange={(e) => setMultaForm({ ...multaForm, data_vencimento: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMultaForm(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarMulta}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VeiculosPage;

